import React, { useState } from 'react';
import axios from 'axios';
import { CheckCircle, Calculator, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Offerte = () => {
  const [formData, setFormData] = useState({
    naam: '',
    bedrijfsnaam: '',
    email: '',
    telefoonnummer: '',
    datum_evenement: '',
    aantal_gasten: '',
    locatie: '',
    adres: '',
    soort_evenement: '',
    aantal_cocktails: '',
    gewenste_cocktails: '',
    opmerkingen: ''
  });

  const [calculatorGasten, setCalculatorGasten] = useState('');
  const [cocktailsPP, setCocktailsPP] = useState('4');
  const [calculatorResult, setCalculatorResult] = useState(null);
  const [calculatorError, setCalculatorError] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCalculate = async () => {
    if (!calculatorGasten || parseInt(calculatorGasten) < 1) {
      setCalculatorError(true);
      setCalculatorResult(null);
      return;
    }

    setCalculatorError(false);
    try {
      const res = await axios.post(`${API_URL}/api/calculator`, {
        aantal_gasten: parseInt(calculatorGasten),
        cocktails_pp: parseInt(cocktailsPP) || 4
      });
      setCalculatorResult(res.data);
    } catch (error) {
      if (error.response?.data?.detail) {
        setCalculatorError(true);
        setCalculatorResult(null);
      } else {
        alert('Er ging iets mis met de berekening');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API_URL}/api/offerte`, {
        ...formData,
        aantal_gasten: parseInt(formData.aantal_gasten),
        aantal_cocktails: formData.aantal_cocktails ? parseInt(formData.aantal_cocktails) : null,
      });
      setSubmitted(true);
      setFormData({
        naam: '',
        bedrijfsnaam: '',
        email: '',
        telefoonnummer: '',
        datum_evenement: '',
        aantal_gasten: '',
        locatie: '',
        adres: '',
        soort_evenement: '',
        aantal_cocktails: '',
        gewenste_cocktails: '',
        opmerkingen: ''
      });
    } catch {
      setError('Er ging iets mis. Probeer het opnieuw of neem direct contact op.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-cm-primary">
        <div className="max-w-2xl mx-auto px-4 text-center text-white">
          <CheckCircle size={80} className="mx-auto mb-6" />
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">Bedankt voor je Aanvraag!</h1>
          <p className="text-xl mb-8">
            We hebben je offerte-aanvraag ontvangen en nemen binnen 24 uur contact met je op.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => setSubmitted(false)} 
              className="btn-secondary"
            >
              Nieuwe Aanvraag
            </button>
            <a href="/" className="btn-primary">
              Terug naar Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="bg-cm-primary text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl sm:text-6xl font-bold mb-6">Offerte Aanvragen</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Vul het formulier in en ontvang binnen 2 werkdagen een vrijblijvende offerte op maat
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 gap-8">
          {/* Offerte Form */}
          <div>
            <div className="bg-white rounded-lg shadow-summer p-8">
              <h2 className="text-3xl font-bold mb-6 text-[#13498f]">Aanvraagformulier</h2>
              
              {error && (
                <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700">
                      Naam *
                    </label>
                    <input
                      type="text"
                      name="naam"
                      value={formData.naam}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#13498f] focus:border-transparent"
                      placeholder="Je voor- en achternaam"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700">
                      Bedrijfsnaam (optioneel)
                    </label>
                    <input
                      type="text"
                      name="bedrijfsnaam"
                      value={formData.bedrijfsnaam}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#13498f] focus:border-transparent"
                      placeholder="Indien van toepassing"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#13498f] focus:border-transparent"
                      placeholder="je@email.nl"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700">
                      Telefoonnummer *
                    </label>
                    <input
                      type="tel"
                      name="telefoonnummer"
                      value={formData.telefoonnummer}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#13498f] focus:border-transparent"
                      placeholder="06 12345678"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700">
                      Datum Evenement *
                    </label>
                    <input
                      type="date"
                      name="datum_evenement"
                      value={formData.datum_evenement}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#13498f] focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700">
                      Aantal Gasten *
                    </label>
                    <input
                      type="number"
                      name="aantal_gasten"
                      value={formData.aantal_gasten}
                      onChange={handleChange}
                      required
                      min="1"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#13498f] focus:border-transparent"
                      placeholder="Bijv. 50"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    Locatie *
                  </label>
                  <input
                    type="text"
                    name="locatie"
                    value={formData.locatie}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#13498f] focus:border-transparent"
                    placeholder="Bijv. thuis, restaurant, feestzaal, buitenlocatie..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    Adres *
                  </label>
                  <input
                    type="text"
                    name="adres"
                    value={formData.adres}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#13498f] focus:border-transparent"
                    placeholder="Straat, huisnummer, postcode en stad"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    Soort Evenement *
                  </label>
                  <select
                    name="soort_evenement"
                    value={formData.soort_evenement}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#13498f] focus:border-transparent"
                  >
                    <option value="">Selecteer type evenement</option>
                    <option value="Bruiloft">Bruiloft</option>
                    <option value="Bedrijfsfeest">Bedrijfsfeest</option>
                    <option value="Verjaardag">Verjaardag</option>
                    <option value="Festival">Festival</option>
                    <option value="Privéfeest">Privéfeest</option>
                    <option value="Anders">Anders</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    Aantal Cocktails (schatting)
                  </label>
                  <input
                    type="number"
                    name="aantal_cocktails"
                    value={formData.aantal_cocktails}
                    onChange={handleChange}
                    min="1"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#13498f] focus:border-transparent"
                    placeholder="Bijv. 200 (laat leeg als je nog niet weet)"
                    data-testid="offerte-aantal-cocktails"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Reken op ongeveer 3-4 cocktails per gast.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    Gewenste Cocktails
                  </label>
                  <textarea
                    name="gewenste_cocktails"
                    value={formData.gewenste_cocktails}
                    onChange={handleChange}
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#13498f] focus:border-transparent"
                    placeholder="Bijv. Mojito, Espresso Martini, Pornstar Martini... of laat het aan ons over!"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    Opmerkingen (optioneel)
                  </label>
                  <textarea
                    name="opmerkingen"
                    value={formData.opmerkingen}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#13498f] focus:border-transparent"
                    placeholder="Extra wensen of opmerkingen..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Bezig met verzenden...' : 'Verstuur Aanvraag'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Offerte;
